/**
 * Keyword search over the Acts (ADR-017). Shared by the offline index builder
 * (packages/corpus) and the Worker, so both read text the same way.
 *
 * Index layout in the originals bucket, under `search/v1/`:
 *   meta.json                      documents, passage table, corpus statistics
 *   shards/<hex>.json              term -> [passage, frequency, passage, frequency, ...]
 *   passages/<versionId>.json      the passages of one Act, in table order
 */

export const SEARCH_INDEX_PREFIX = 'search/v1';
export const SEARCH_SHARDS = 512;

/** Words too common in legislation to help find a passage. */
const STOPWORDS = new Set(
  (
    'a an and are as at be been being but by can could did do does for from had has have he her his ' +
    'how i if in into is it its may me might must my no not of on or our shall she should so such ' +
    'than that the their them then there these they this those to under unless upon was we were what ' +
    'when where which who whom whose why will with would you your any all each every other same said ' +
    'act acts section sections subsection subsections paragraph provided hereby herein thereof therein ' +
    'whereas also only than about after before over within without between out up down per ' +
    // In a library of Sierra Leone's laws these appear everywhere and distinguish nothing.
    'sierra leone'
  ).split(' '),
);

/** Separate run-together words in file-name style titles: "CompaniesAct2009" -> "Companies Act 2009". */
export function splitJoinedWords(text: string): string {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2');
}

/** A readable Act title: no "Act N0.5-" file prefix, run-together words separated. */
export function displayTitle(title: string): string {
  const cleaned = splitJoinedWords(title.replace(/^Act\s*N[0O]\.?\s*\d+\s*[-–]\s*/i, ''))
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || title;
}

/** Lower-case word tokens, at least two characters, without stopwords. */
export function tokenize(text: string): string[] {
  const words =
    splitJoinedWords(text)
      .toLowerCase()
      .match(/[a-z0-9]+/g) ?? [];
  return words.filter((w) => w.length >= 2 && !STOPWORDS.has(w)).map(normalise);
}

/**
 * Light stemming, so related forms meet: "companies"/"company", "regulates"/"regulation"/
 * "regulated"/"regulating", "offences"/"offence". Deliberately simple and predictable.
 */
function normalise(word: string): string {
  let w = word;
  if (/^\d+$/.test(w)) return w;
  if (w.length > 4 && w.endsWith('ies')) w = `${w.slice(0, -3)}y`;
  else if (/(ss|x|ch|sh)es$/.test(w) && w.length > 4) w = w.slice(0, -2);
  else if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) w = w.slice(0, -1);
  if (w.length > 7 && w.endsWith('ation')) w = w.slice(0, -3);
  else if (w.length > 5 && w.endsWith('ing')) w = w.slice(0, -3);
  else if (w.length > 4 && w.endsWith('ed') && !w.endsWith('eed')) w = w.slice(0, -2);
  if (w.length > 4 && w.endsWith('e')) w = w.slice(0, -1);
  return w;
}

/** Which shard holds a term: FNV-1a hash, so builder and Worker agree. */
export function shardOf(term: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < term.length; i += 1) {
    hash ^= term.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return (hash % SEARCH_SHARDS).toString(16).padStart(3, '0');
}

export type SearchMeta = {
  version: 1;
  builtAt: string;
  /** Number of passages and their mean length in tokens (BM25). */
  passageCount: number;
  averageLength: number;
  /**
   * [versionId, sourceId, title, year, rightsStatus, collection ("acts" when absent),
   *  judgments only: how many other judgments name its parties, and the name words used]
   */
  documents: [
    string,
    string,
    string,
    number | null,
    string,
    SearchCollection?,
    number?,
    string[]?,
  ][];
  /** [document index, length in tokens, position within that document's passage file] */
  passages: [number, number, number][];
};

export type SearchShard = Record<string, number[]>;

/** Acts (statutes) or cases (judgments, ADR-018). */
export type SearchCollection = 'acts' | 'cases';

export type StoredPassage = {
  /** The passage's real ID; chunks of one long passage share it. */
  id: string;
  /** Distinguishes chunks of a long passage ("psg_…~2"); absent when it is whole. */
  key?: string;
  label: string;
  text: string;
  pageStart: number;
  pageEnd: number;
};
